<template>
  <div :class="`as-filter-tags as-filter-tags-${siteName}`">
    <div v-if="tags.length" class="as-filter-tags-top">
      <div class="as-filter-tags__day">
        <div class="as-today" @click="setTagsToday()">
          <IconToday fill="#00FFFD" fillReact="#00FFFD" />
          Hôm nay
        </div>
        <div class="as-calendar">
          <div class="as-calendar-text">Cụ thể</div>
          <DatePicker
            v-model:value="selectedDate"
            :format="dateFormat"
            :disabled-date="disableFutureDates"
            placeholder="Chọn ngày"
            @change="handleDateChange"
            :popup-class="`api-sport api-sport-${siteName}`"
            :input-attr="{ readonly: true }"
          />
        </div>
      </div>
      <div class="as-sort-by" v-if="isShowSort && filter.tag">
        <span class="as-title"> Sắp xếp: </span>
        <div @click="sorByTime()">
          <span class="as-with-by">
            Theo {{ sortBy == "league" ? "thời gian" : "giải đấu" }}
          </span>
          <IConSort />
        </div>
      </div>
    </div>
    <div class="as-filter-country-top" v-if="countries.length">
      <div class="as-item-country as-item-country-text">Quốc gia</div>
      <div
        class="as-item-country as-item-country-select"
        @click="toggleContentCountries"
        ref="selectHeader"
      >
        <div class="as-item-country-select-header">
          <div>
            <img
              :src="getImgUrlBySport('country', filter.country?.id)"
              :alt="filter.country?.name_vi ?? ''"
              v-if="filter.country?.id"
            />
            <span>{{
              filter.country
                ? filter.country?.name_vi ?? filter.country?.name
                : "Tất cả"
            }}</span>
          </div>
          <IconCustomArrownDown
            v-if="!isShowContentCountries"
            fill="#2FB925"
            @click.stop="toggleContentCountries"
          />
          <IconCustomArrownUp
            fill="#2FB925"
            v-else
            @click.stop="toggleContentCountries"
          />
        </div>
        <div
          class="as-item-country-select-content"
          v-if="isShowContentCountries"
          ref="selectContent"
        >
          <div class="as-select-content">
            <div
              :class="{
                'as-active': !filter.country?.id,
              }"
              @click="setActiveCountry(null)"
            >
              Tất cả
            </div>
            <div
              :key="country.id"
              v-for="(country, index) in countries"
              @click="setActiveCountry(country)"
              :class="{
                'as-active': filter.country?.id == country.id,
              }"
            >
              {{ country.name_vi ?? country.name }}
            </div>
          </div>
        </div>
      </div>
    </div>
    <div v-if="leagues.length" class="as-filter-tags__leagues">
      <div class="as-tags-leagues" :class="{ 'as-is-expanded': isExpanded }">
        <!-- <a class="as-league-item" role="button" :class="{
          'active as-active': !filter?.league?.cur_season_id
        }" @click=setTagsToday(true)>Tất cả</a> -->
        <a
          v-for="league in leagues"
          :key="league?.id"
          class="as-league-item"
          :class="{
            'active as-active':
              league?.cur_season_id === filter?.league?.cur_season_id,
          }"
          @click="() => changeLeague(league)"
          href="javascript:void(0)"
        >
          <span class="as-before-extra"></span>
          {{ league.short_name_vi ? league.short_name_vi : league.short_name }}
        </a>
      </div>
      <div class="as-expand" role="button" @click="toggleExpand">
        <IconArrownDown v-if="!isExpanded" />
        <IconArrownUp v-if="isExpanded" />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import type { ITag, ILeague, IFilterResult, ICountry } from "~src/interfaces";
import IconToday from "./icons/IconToday.vue";
import DatePicker from "vue-datepicker-next";
import { ref, computed } from "vue";
import IconArrownUp from "./icons/IconArrownUp.vue";
import IconArrownDown from "./icons/IconArrownDown.vue";
import IConSort from "./icons/IConSort.vue";
import { usecommonStore } from "~src/stores/CommonStore";
import IconCustomArrownDown from "~src/components/icons/IconCustomArrownDown.vue";
import IconCustomArrownUp from "~src/components/icons/IconCustomArrownUp.vue";
import { useClickOutside } from "~src/utils/clickOutsideHelper";
const commonStore = usecommonStore();

const emits = defineEmits([
  "changeTag",
  "changeLeague",
  "sorByTime",
  "changeCountry",
]);

interface IProps {
  tags: Array<ITag>;
  leagues: Array<ILeague>;
  countries: Array<ICountry>;
  filter: IFilterResult;
  sortBy: string;
  isShowSort: boolean;
  page?: string;
}

const props = withDefaults(defineProps<IProps>(), {
  isShowSort: true,
  countries: () => [] as ICountry[],
});

const selectHeader = ref<HTMLElement | null>(null);
const selectContent = ref<HTMLElement | null>(null);
const siteName = computed(() => commonStore.getSiteName);
const selectedDate = ref<Date>(new Date());
const dateFormat = "DD/MM/YYYY";
const isExpanded = ref(false);
const isShowContentCountries = ref(false);
const countryActive = ref<ICountry | null>(null);

const toggleExpand = () => {
  isExpanded.value = !isExpanded.value;
};

const formatDateToYYYYMMDD = (date: Date): any => {
  const year = date.getFullYear();
  const month = (date.getMonth() + 1).toString().padStart(2, "0");
  const day = date.getDate().toString().padStart(2, "0");

  return {
    day,
    month,
    year,
  };
};

const handleDateChange = (newDate: Date) => {
  selectedDate.value = newDate;
  const _value = formatDateToYYYYMMDD(newDate);
  const tag: ITag = {
    title: `${_value.day}/${_value.day}`,
    value: `${_value.year}-${_value.month}-${_value.day}`,
  };

  emits("changeTag", tag);
};

const disableFutureDates = (date: Date) => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  if (props.page == "result") {
    return date > today;
  }

  return date < today;
};

const changeTag = (tag: ITag) => {
  emits("changeTag", tag);
};

const changeLeague = (league: ILeague) => {
  emits("changeLeague", league);
};

const sorByTime = () => {
  emits("sorByTime", props.sortBy === "time" ? "league" : "time");
};

const setTagsToday = (is_all: boolean = false) => {
  const newDate = new Date();
  if (!is_all) {
    selectedDate.value = newDate;
  }

  const _value = formatDateToYYYYMMDD(newDate);
  const tag: ITag = {
    title: `${_value.day}/${_value.day}`,
    value: `${_value.year}-${_value.month}-${_value.day}`,
  };

  emits("changeTag", tag);
};

const setActiveCountry = (country: ICountry | null) => {
  isShowContentCountries.value = false;
  emits("changeCountry", country);
};

const toggleContentCountries = () => {
  isShowContentCountries.value = !isShowContentCountries.value;
};

const closeCountrySelect = () => {
  isShowContentCountries.value = false;
};

useClickOutside([selectHeader, selectContent], {
  handler: closeCountrySelect,
  middleware: (event) => {
    const target = event.target as HTMLElement;

    if (target.closest(".icon-arrow")) {
      return false;
    }

    return true;
  },
});
</script>

<script lang="ts">
export default {};
</script>

<style scoped></style>
